/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  WATCH_STATUS_ON, WATCH_STATUS_OPEN, WATCH_STATUS_CLOSED,
  WATCH_STATUS_BLOCKED, WATCH_TYPE_CALL, WATCH_TYPE_SMS,
  ALL, WATCH_STATUS_CALLS_IN, WATCH_STATUS_CALLS_OUT,
} from '../../Constants';
import Switch from '../Switch';
import Select from '../Select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isSectionDisabled } from '../../Helper';
import '../../styles.css';
import { Dialog } from '@mui/material';

const STATUS_CONFIG = [
  {
    value: WATCH_STATUS_OPEN,
    label: 'כל השיחות פעילות',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M8 12l2 2 4-4"/>
      </svg>
    ),
  },
  {
    value: WATCH_STATUS_BLOCKED,
    label: 'כל השיחות חסומות',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
      </svg>
    ),
  },
  {
    value: WATCH_STATUS_ON,
    label: 'לפי הגדרות השעון',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
  },
];

export default function WatchSystemDesktop(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [excludedInfoOpen, setExcludedInfoOpen] = useState(false);
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  const [showInactiveNotice, setShowInactiveNotice] = useState(false);
  const [mobileTab, setMobileTab] = useState('hours'); // mobile: 'hours' | 'excluded'
  const [scopeNoteOpen, setScopeNoteOpen] = useState(true);
  const statusChangedByUser = useRef(false);

  useEffect(() => {
    if (!scopeNoteOpen) return undefined;
    const id = setTimeout(() => setScopeNoteOpen(false), 5000);
    return () => clearTimeout(id);
  }, [scopeNoteOpen]);

  // Alert only when ENTERING the watch in a non-schedule mode — not when the
  // user just switched the status here (that flow shows the "saved" popup).
  useEffect(() => {
    if (statusChangedByUser.current) {
      statusChangedByUser.current = false;
      return;
    }
    if (props.watchStatus && props.watchStatus !== WATCH_STATUS_ON) {
      setShowInactiveNotice(true);
    }
  }, [props.watchStatus]);
  /* ── MINUTE-LEVEL SCHEDULING (disabled) ────────────────────────────────
  const [expandedHour, setExpandedHour] = useState(null);

  const getMinuteData = (day, hour, minute) =>
    props.watchProfile?.[day]?.[hour]?.[minute.toString()] ?? null;

  const isMinuteActive = (day, hour, minute) => {
    const md = getMinuteData(day, hour, minute);
    if (!md) return true;
    return md.status === WATCH_STATUS_OPEN;
  };

  const hasMinuteOverrides = (day, hour) => {
    const hourData = props.watchProfile?.[day]?.[hour] ?? {};
    return Object.keys(hourData).some(k => {
      const n = parseInt(k);
      return !isNaN(n) && n >= 0 && n < 60;
    });
  };

  const toggleMinute = (day, hour, minute) => {
    const hourData = props.watchProfile?.[day]?.[hour] ?? {};
    const calls = hourData.calls ?? ALL;
    const currentlyOpen = isMinuteActive(day, hour, minute);
    props.changeWatchProfile(
      { calls, status: currentlyOpen ? WATCH_STATUS_CLOSED : WATCH_STATUS_OPEN },
      minute.toString(),
      day,
      hour
    );
  };

  const selectAllMinutes = (day, hour) => {
    const hourData = props.watchProfile?.[day]?.[hour] ?? {};
    const clean = {};
    Object.keys(hourData).forEach(k => {
      const n = parseInt(k);
      if (isNaN(n) || n < 0 || n >= 60) clean[k] = hourData[k];
    });
    props.changeHourData(clean, day, hour);
  };

  const clearAllMinutes = (day, hour) => {
    const hourData = props.watchProfile?.[day]?.[hour] ?? {};
    const calls = hourData.calls ?? ALL;
    const newHour = { ...hourData };
    for (let m = 0; m < 60; m++) {
      newHour[m.toString()] = { calls, status: WATCH_STATUS_CLOSED };
    }
    props.changeHourData(newHour, day, hour);
  };

  const renderMinuteGrid = (day, hour) => (
    <div className='ws-minute-grid'>
      <div className='ws-minute-grid-header'>
        <span className='ws-minute-grid-label'>בחר דקות פעילות בשעה זו</span>
        <div className='ws-minute-grid-actions'>
          <button className='ws-minute-grid-btn' onClick={(e) => { selectAllMinutes(day, hour); e.stopPropagation(); }}>
            בחר הכל
          </button>
          <button className='ws-minute-grid-btn' onClick={(e) => { clearAllMinutes(day, hour); e.stopPropagation(); }}>
            נקה הכל
          </button>
        </div>
      </div>
      <div className='ws-minute-cells'>
        {Array.from({ length: 60 }, (_, m) => (
          <button
            key={m}
            className={`ws-minute-cell${isMinuteActive(day, hour, m) ? ' active' : ''}`}
            onClick={(e) => { toggleMinute(day, hour, m); e.stopPropagation(); }}
          >
            {String(m).padStart(2, '0')}
          </button>
        ))}
      </div>
    </div>
  );
  ────────────────────────────────────────────────────────────────────── */

  const renderHoursCol = (dayIndex, start, end) =>
    Array.from({ length: end - start + 1 }, (_, i) => start + i).map(h => {
      const hourData = props.watchProfile?.[dayIndex]?.[h] ?? {};
      const isOpen = (hourData.status ?? WATCH_STATUS_OPEN) === WATCH_STATUS_OPEN;

      return (
        <div key={h} className={`ws-hour-row ${isOpen ? 'is-open' : 'is-closed'}`}>
          <div className='ws-hour-main'>
            <div className='ws-hour-time'>
              <span className='ws-hour-num'>{String(h).padStart(2, '0')}:00</span>
            </div>
            <div className='ws-hour-cfg'>
              <span className='ws-hour-cfg-label'>הגדרה עבור:</span>
              <Select
                values={{
                  [ALL]: (
                    <span className='ws-calls-opt'>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                        <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                      </svg>
                      כל השיחות
                    </span>
                  ),
                  [WATCH_STATUS_CALLS_IN]: (
                    <span className='ws-calls-opt'>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                      </svg>
                      שיחות נכנסות
                    </span>
                  ),
                  [WATCH_STATUS_CALLS_OUT]: (
                    <span className='ws-calls-opt'>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                      </svg>
                      שיחות יוצאות
                    </span>
                  ),
                }}
                value={hourData.calls ?? ALL}
                handleClick={(val, e) => { props.changeWatchProfile(val, 'calls', dayIndex, h); e.stopPropagation(); }}
              />
            </div>
            <Switch
              type="text"
              name="status"
              values={{ [WATCH_STATUS_CLOSED]: t('Watch Inactive'), [WATCH_STATUS_OPEN]: t('Watch Active') }}
              value={hourData.status ?? WATCH_STATUS_OPEN}
              mainValue={WATCH_STATUS_OPEN}
              handleClick={(val, e) => { props.changeWatchProfile(val, 'status', dayIndex, h); e.stopPropagation(); }}
            />
          </div>
        </div>
      );
    });

  return (
    <div className='ws-page'>

      {/* ── Error dialog ── */}
      <Dialog open={!!props.showErrorMessage} onClose={() => props.setShowErrorMessage(false)} PaperProps={{ className: 'err-dialog-paper' }}>
        <div className='err-dialog'>
          <div className='err-dialog-header'>
            <div className='err-dialog-icon'>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <h3 className='err-dialog-title'>שגיאה</h3>
          </div>
          <div className='err-dialog-body'>
            <p className='err-dialog-text'>{props.errorMessage}</p>
            <button className='err-dialog-btn' onClick={() => props.setShowErrorMessage(false)}>{t('Close')}</button>
          </div>
        </div>
      </Dialog>

      {/* ── Warning alert (inline — contains a list) ── */}
      {props.showWarningMessage && (
        <div className='ws-alert ws-alert-warning'>
          <div>
            <div className='ws-alert-title'>{props.warningMessageTitle}</div>
            <ul className='ws-alert-list'>{props.warningMessageErrorLines()}</ul>
          </div>
          <button className='ws-alert-close' onClick={() => props.setShowWarningMessage(false)}>✕</button>
        </div>
      )}

      {/* ── Page header ── */}
      <div className='ws-header'>
        <div className='ws-header-right'>
          <div className='ws-header-icon'>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div>
            <div className='ws-header-title'>מערכת השעון</div>
            <div className='ws-header-sub'>ניהול שעות פעילות וחסימה לפי יום</div>
          </div>
        </div>
        {/* Watch type — choose which system you manage (right side of the header) */}
        {props.displayWatchTypeSwitch && (
          <div className='ws-type-row'>
            <span className='ws-type-label'>איזו מערכת לנהל?</span>
            <div className='ws-type-pills'>
              <button
                className={`ws-type-pill${props.watchType === WATCH_TYPE_CALL ? ' active' : ''}`}
                onClick={() => props.setWatchType(WATCH_TYPE_CALL)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                מערכת שיחות
              </button>
              <button
                className={`ws-type-pill${props.watchType === WATCH_TYPE_SMS ? ' active' : ''}`}
                onClick={() => props.setWatchType(WATCH_TYPE_SMS)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                מערכת SMS
              </button>
            </div>
          </div>
        )}

        <div className='ws-status-group'>
          <span className='ws-status-label'>סטטוס מערכת</span>
          <div className='ws-status-pills'>
            {STATUS_CONFIG.map(opt => (
              <button
                key={opt.value}
                className={`ws-status-pill${props.watchStatus === opt.value ? ' ws-status-pill-active' : ''}`}
                onClick={() => {
                  if (props.watchStatus !== opt.value) {
                    statusChangedByUser.current = true;
                    props.setWatchStatus(opt.value);
                    props.setIsWatchDataModified(true);
                  }
                }}
              >
                {opt.icon}
                {opt.label}
              </button>
            ))}
          </div>
          {/* mobile: status as a dropdown */}
          <select
            className='ws-status-select psh-select'
            value={props.watchStatus}
            onChange={(e) => { statusChangedByUser.current = true; props.setWatchStatus(e.target.value); props.setIsWatchDataModified(true); }}
          >
            {STATUS_CONFIG.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── scope notice popup: community-wide only, and only if the community may access the members list ── */}
      {scopeNoteOpen && props.isCommunityWide && !isSectionDisabled('members') && createPortal(
        <div className='ws-scope-pop'>
          <button className='ws-scope-pop-close' onClick={() => setScopeNoteOpen(false)} title='סגירה'>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <span className='ws-scope-pop-icon'>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          </span>
          <span className='ws-scope-pop-text'>
            כאן מנהלים את זמני הפעילות עבור <strong>כל הקהילה</strong>. כדי להגדיר זמני פעילות או רשימות חסימה <strong>למנוי מסוים</strong>,&nbsp;
            <button type='button' className='ws-scope-pop-link' onClick={() => navigate('/customers-management')}>לחצו כאן</button>
            &nbsp;למעבר לניהול המנויים.
          </span>
          <span className='ws-scope-pop-bar'><i /></span>
        </div>,
        document.body
      )}

      {/* mobile tabs: switch between hours and excluded numbers */}
      <div className='ws-mtabs'>
        <button className={'ws-mtab' + (mobileTab === 'hours' ? ' active' : '')} onClick={() => setMobileTab('hours')}>לוח שעות</button>
        <button className={'ws-mtab' + (mobileTab === 'excluded' ? ' active' : '')} onClick={() => setMobileTab('excluded')}>מספרים מוחרגים</button>
      </div>

      {/* ── Body: hours (right) + excluded (left) ── */}
      <div className={'ws-body ws-body--' + mobileTab}>

        {/* Hours card */}
        <div className='ws-card ws-hours-card'>
          <div className='ws-card-head'>
            <h3 className='ws-card-title'>לוח שעות</h3>
            {props.watchStatus !== WATCH_STATUS_ON && (
              <span className='ws-inactive-badge'>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                {props.watchStatus === WATCH_STATUS_OPEN ? 'כל השיחות פעילות — הלוח נעול לעריכה' : 'כל השיחות חסומות — הלוח נעול לעריכה'}
              </span>
            )}
          </div>
          <div className='ws-days-bar ws-days-bar--full'>{props.getDayLabels()}</div>
          <div className='ws-days-bar ws-days-bar--letters'>{props.getDayLabels(true)}</div>
          <div className={`ws-hours-grid${props.watchStatus === WATCH_STATUS_OPEN ? ' ws-hours-all-open' : props.watchStatus === WATCH_STATUS_BLOCKED ? ' ws-hours-all-blocked' : ''}`}>
            <div className='ws-hours-col'>{renderHoursCol(props.selectedDayIndex, 0, 11)}</div>
            <div className='ws-hours-divider' />
            <div className='ws-hours-col'>{renderHoursCol(props.selectedDayIndex, 12, 23)}</div>
          </div>
        </div>

        {/* Excluded numbers card */}
        <div className='ws-excluded'>
          <div className='ws-excluded-head'>
            <div>
              <h3 className='ws-excluded-title'>{t('Excluded numbers from the watch:')}</h3>
              <p className='ws-excluded-sub'>מספרים שיוכלו לחייג ויהיה ניתן לחייג אליהם גם כאשר מערכת השעון פעילה</p>
            </div>
            <div className='ws-excluded-head-actions'>
              {!props.addNumberInputVisible && (
                <button className='ws-add-btn' onClick={() => props.setAddNumberInputVisible(true)}>
                  + {t('Add number')}
                </button>
              )}
              <div className='ws-import-wrap'>
                <button
                  className={'ws-import-btn' + (importMenuOpen ? ' is-open' : '')}
                  onClick={() => setImportMenuOpen(o => !o)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  העלאת רשימת מספרים
                  <svg className='ws-import-chev' width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {importMenuOpen && (
                  <>
                    <div className='ws-import-overlay' onClick={() => setImportMenuOpen(false)} />
                    <div className='ws-import-menu'>
                      <button className='ws-import-item' onClick={() => { setImportMenuOpen(false); document.getElementById('excluded_import_file').click(); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        העלאת קובץ
                      </button>
                      <a className='ws-import-item' href='/מספרים מוחרגים לדוגמה.csv' download onClick={() => setImportMenuOpen(false)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        הורדת קובץ דוגמה
                      </a>
                    </div>
                  </>
                )}
                <input id='excluded_import_file' type='file' accept='.csv,.txt' style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files[0]; e.target.value = null; props.handleImportExcludedNumbers(f); }} />
              </div>
            </div>
          </div>

          {/* explanation — collapsible */}
          <div className={'ws-excluded-info' + (excludedInfoOpen ? ' open' : '')}>
            <button type='button' className='ws-excluded-info-toggle' onClick={() => setExcludedInfoOpen(o => !o)}>
              <span className='ws-excluded-info-icon'>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
              </span>
              <span className='ws-excluded-info-q'>מה זה החרגת מספר?</span>
              <svg className='ws-excluded-info-chev' width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {excludedInfoOpen && (
              <div className='ws-excluded-info-text'>
                <p>כאן ניתן להוסיף מספרי טלפון שיוכלו <strong>לחייג לקהילה בכל עת</strong> — גם בשעות שהוגדרו בלוח כשעות לא פעילות (כבויות), ללא כל הגבלה.</p>
                <p>בנוסף, מספרים אלה <strong>פטורים מהחסימה גם בכיוון ההפוך</strong> — כלומר ניתן לחייג אליהם מתוך הקהילה בכל שעה. מומלץ להוסיף כאן מספרים של <strong>גבאים, רבנים ואנשי קשר חיוניים</strong> שזקוקים לזמינות מלאה מסביב לשעון.</p>
              </div>
            )}
          </div>
          {props.addNumberInputVisible && (
            <div className='ws-add-input-row'>
              <div className='ws-add-input-fields'>
                <input type='text' id='excluded_number_name' className='ws-input' placeholder='שם (אופציונלי)' />
                <input type='text' id='excluded_numbers' className='ws-input' placeholder='מספר טלפון...'
                  onKeyDown={(e) => { if (e.key === 'Enter') props.handleAddExcludedNumber(); }} />
              </div>
              <div className='ws-add-input-row-actions'>
                <button className='ws-add-confirm' onClick={() => props.handleAddExcludedNumber()}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  הוסף
                </button>
                <button className='ws-add-cancel' onClick={() => props.setAddNumberInputVisible(false)}>ביטול</button>
              </div>
            </div>
          )}
          <div className='ws-chips'>{props.getExcludedNumbersChips()}</div>
        </div>

      </div>

      {/* ── Saving toast (bottom-right, indeterminate line) ── */}
      {props.saving && createPortal(
        <div className='ws-savedtoast ws-savedtoast--saving'>
          <div className='ws-savedtoast-row'>
            <span className='ws-saving-spinner' />
            <span>שומר שינויים...</span>
          </div>
          <div className='ws-savedtoast-line'><span className='indet' /></div>
        </div>,
        document.body
      )}

      {/* ── Success toast (bottom-right, with filling line) ── */}
      {props.showSuccessMessage && createPortal(
        <div className='ws-savedtoast'>
          <div className='ws-savedtoast-row'>
            <span className='ws-savedtoast-check'>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </span>
            <span>השינויים נשמרו בהצלחה</span>
          </div>
          <div className='ws-savedtoast-line'><span /></div>
        </div>,
        document.body
      )}

      {/* ── Apply dialog ── */}
      {/* ── Watch mode inactive notice ── */}
      <Dialog open={showInactiveNotice} onClose={() => setShowInactiveNotice(false)} PaperProps={{ className: 'ws-dialog-paper' }}>
        <div className='ws-notice'>
          <div className='ws-notice-icon'>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <h3 className='ws-notice-title'>שימו לב, מצב שעון אינו פעיל</h3>
          <p className='ws-notice-text'>כדי לערוך את הגדרות השעון יש להפעיל את מצב השעון (לפי לוח זמנים).</p>
          <button className='ws-notice-btn' onClick={() => setShowInactiveNotice(false)}>הבנתי</button>
        </div>
      </Dialog>

      <Dialog open={props.applyWatchSettingsDialogIsOpen} fullWidth PaperProps={{ className: 'ws-dialog-paper' }}>
        <div className='ws-dialog'>
          <h3 className='ws-dialog-title'>האם לשמור את ההגדרה עבור ימים נוספים?</h3>
          <p className='ws-dialog-note'>בחרו את הימים עבורם תרצו לשמור את ההגדרות</p>
          <div className='ws-dialog-days'>{props.getApplyDays()}</div>
          <div className='ws-dialog-actions'>
            <button className='ws-dialog-btn-confirm' onClick={props.applyProfileChangesOnSelectedDays}>{t('Yes')}</button>
            <button className='ws-dialog-btn-cancel' onClick={() => {
              props.setApplyWatchSettingsDialogIsOpen(false);
              props.setSelectedDayIndex(props.nextSelectedDayIndex);
            }}>{t('No')}</button>
          </div>
        </div>
      </Dialog>

    </div>
  );
}
